// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { Location } from "@insite/client-framework/Components/SpireRouter";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HeaderClickable from "@stanion/App/Components/Header/HeaderClickable";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { xIsAuthenticated } from "@stanion/App/Store/Selectors/GeneralSelectors";
import { XNGAGE_CUSTOM_WIDGET } from "@stanion/XDL/constants";
import RecentPurchases from "@stanion/XDL/Icons/RecentPurchases";
import { color, layoutContainer } from "@stanion/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    recentPurchasedUrl: getPageLinkByPageType(state, "RecentPurchasedProductsPage")?.url,
    isTabletScreen: state.xCommon.isTabletScreen,
    isRecentPurchasedProductsPage: getCurrentPage(state).type === "RecentPurchasedProductsPage",
    currentLocation: getLocation(state),
    isAuthenticated: xIsAuthenticated(state),
    recentPurchasedProductsPageUrl: getPageLinkByPageType(state, "RecentPurchasedProductsPage")?.url,
});

export interface RecentPurchasedStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
}
interface OwnProps extends WidgetProps {
    fields: {};
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
        css: css`
            padding: 0;
            &:hover {
                background-color: ${color("common.white")};
                border-color: ${color("common.white")};
            }
        `,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            padding-bottom: 5px;
            margin: 0 45px;
            display: flex;
            justify-content: center;
        `,
        drawerBody: css`
            background: ${color("common.background")};
            height: auto;
            max-height: 100vh;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            ${layoutContainer};
            background: ${color("common.background")};
            width: 100%;

            & ${TypographyStyle} {
                margin: 0;
            }
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const recentPurchasedLinkStyles: RecentPurchasedStyles = {
    title: {
        size: 14,
        weight: "bold",
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
        `,
    },
    wrapper: {
        css: css`
            .arrow-menu-icon {
                visibility: hidden;
            }
        `,
    },
    drawer: baseDrawerStyles,
    container: {
        css: css`
            ${layoutContainer}
            padding-bottom: 10px;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const RecentlyPurchasedLink: FC<Props> = ({
    id,
    recentPurchasedUrl,
    isTabletScreen,
    isRecentPurchasedProductsPage,
    currentLocation,
    isAuthenticated,
    recentPurchasedProductsPageUrl,
}) => {
    const history = useHistory();

    const [isPopupShown, setIsPopupShown] = useState(false);
    const [prevLocation, setPrevLocation] = useState<Location>();

    // close drawer on redirection.
    useEffect(() => {
        setPrevLocation(currentLocation);
        if (prevLocation !== currentLocation) {
            setIsPopupShown(false);
        }
    }, [currentLocation]);

    if (!isAuthenticated) {
        return null;
    }

    const clickHandler = () => {
        isTabletScreen ? history.push(`${recentPurchasedUrl}`) : setIsPopupShown(!isPopupShown);
    };

    const styles = recentPurchasedLinkStyles;

    const headerClickableElement = (
        <HeaderClickable
            onClick={clickHandler}
            iconProps={{ src: RecentPurchases }}
            data-test-selector="RecentlyPurchasedLink"
            aria-label="RecentlyPurchasedLink"
            variant="menu"
        >
            <StyledWrapper>
                <Typography as="span" {...styles.title}>
                    {` ${translate("Recently Purchased")} `}
                </Typography>
            </StyledWrapper>
        </HeaderClickable>
    );

    if (isRecentPurchasedProductsPage || isTabletScreen) {
        return headerClickableElement;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {headerClickableElement}
            <Drawer
                {...styles.drawer}
                position="top"
                isOpen={isPopupShown}
                headline={translate("Recently Purchased Products")}
                handleClose={clickHandler}
                data-test-selector="addressDrawer"
            >
                <GridContainer {...styles.container}>
                    <GridItem width={12}>
                        {createWidgetElement("Common/PurchasedProducts", {
                            fields: {
                                purchaseType: "recently",
                                showEmptyErrorMessage: true,
                                showBrand: true,
                                showPartNumbers: true,
                                showPrice: true,
                                showAvailability: true,
                                showAddToCart: true,
                            },
                        })}
                    </GridItem>
                    <GridItem width={12}>
                        <Clickable href={recentPurchasedProductsPageUrl}>
                            <Button>{translate("View Recently Purchased Products")}</Button>
                        </Clickable>
                    </GridItem>
                </GridContainer>
            </Drawer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RecentlyPurchasedLink),
    definition: {
        displayName: "Recently Purchased Link",
        group: XNGAGE_CUSTOM_WIDGET,
    },
};

export default widgetModule;
